import RightArrow from '@public/icons/arrow-small-right.svg';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useState } from 'react';

import type { ExpertT, ImageT } from '@/utils/contentfulTypes';

interface BackedByExpertsProps {
  data: ExpertT[];
  title: string;
  logos: ImageT[];
}

const BackedByExperts: React.FC<BackedByExpertsProps> = ({ data, title, logos }) => {
  const [currExpert, setCurrExpert] = useState<ExpertT>(data[0]);
  const nextExpert = () => {
    const nextIndex = data.indexOf(currExpert) + 1;
    if (nextIndex < data.length) {
      setCurrExpert(data[nextIndex]);
    } else {
      setCurrExpert(data[0]);
    }
  };
  const prevExpert = () => {
    const prevIndex = data.indexOf(currExpert) - 1;
    if (prevIndex >= 0) {
      setCurrExpert(data[prevIndex]);
    } else {
      setCurrExpert(data[data.length - 1]);
    }
  };

  return (
    <section className="py-16 px-4 bg-white text-alter-black font-light md:px-20 md:py-20">
      <div className="width-1440">
        <div className="space-y-10 mt-6 lg:flex lg:flex-row lg:gap-8 lg:space-y-0">
          <div className="flex flex-col min-h-[525px] lg:basis-7/12">
            <h2 className="font-light text-[36px] leading-tight max-w-[16ch] sm:max-w-none md:text-5xl md:leading-snug">
              {title}
            </h2>
            <div className="mt-6 text-2xl md:text-[32px] lg:mt-12">{currExpert.name}</div>
            <p className="mt-4 text-alter-black-80 md:text-lg md:mt-6">{currExpert.description}</p>
            <div className="flex gap-4 mt-4">
              {logos.length > 0 &&
                logos.map((logo) => (
                  <Image
                    key={logo.url}
                    src={logo.url}
                    alt={'company'}
                    height={32}
                    width={80}
                    className="object-contain max-h-[36px]"
                  />
                ))}
            </div>

            <div className="flex justify-between mt-6 md:mt-auto">
              <div className="flex gap-2 mt-4">
                {data.map((expert, index) => (
                  <button
                    key={expert.name}
                    className={clsx(
                      'h-2 w-2 rounded-full',
                      index === data.indexOf(currExpert) ? 'bg-alter-black' : 'bg-alter-black-10'
                    )}
                    aria-label={`Go to expert ${currExpert.name}`}
                    onClick={() => setCurrExpert(data[index])}
                  />
                ))}
              </div>
              <div className="flex gap-4">
                <button
                  className="pagination-arrow-container"
                  onClick={prevExpert}
                  aria-label="Previous expert"
                >
                  <Image
                    src={RightArrow}
                    alt="Previous expert"
                    width={24}
                    height={24}
                    className="rotate-180 mx-auto"
                  />
                </button>
                <button
                  className="pagination-arrow-container"
                  onClick={nextExpert}
                  aria-label="Next expert"
                >
                  <Image
                    src={RightArrow}
                    alt="Previous expert"
                    width={24}
                    height={24}
                    className="mx-auto"
                  />
                </button>
              </div>
            </div>
          </div>
          <Image
            src={currExpert.image.url}
            alt={currExpert.name}
            height={500}
            width={600}
            className="max-h-[500px] object-cover object-[40%_10%] mx-auto rounded-2xl aspect-square md:max-h-none lg:basis-5/12"
          />
        </div>
      </div>
    </section>
  );
};

export default BackedByExperts;
